import { useState, useEffect, useMemo } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import GroupedBarChart from '../components/charts/GroupedBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, IdCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Rec helpers ──────────────────────────────────────────────

const DELETE_RECS = ['DELETE', 'REMOVE', 'STOP', 'REMOVEUNUSEDRESOURCE'];

const resolveRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (DELETE_RECS.includes(t))                              recPillClass = 'rec-id';
  else if (['DOWNGRADE', 'DOWNSIZE', 'RIGHTSIZE'].includes(t)) recPillClass = 'rec-rs';
  return { recType: t || 'OPTIMAL', recPillClass };
};

const notNA = (v) => (v != null && String(v).trim() !== '' && String(v).trim().toUpperCase() !== 'NA') ? v : null;
const toNum  = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

// ── Row Mapper ────────────────────────────────────────────────

const toStaticWebAppRow = (item) => {
  const current     = parseCost(item.currentMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue);
  const saving      = parseCost(item.savingCost ?? Math.max(0, current - recommended));

  const rawRec = item.recommendationType ?? (saving > 0 ? 'DOWNGRADE' : 'OPTIMAL');
  const { recType, recPillClass } = resolveRec(rawRec);

  // Show backend detail exactly; NA / absent falls through, finally "—".
  const remark = notNA(item.recommendationDetail) ?? notNA(item.remark) ?? '—';

  return {
    id:                   item.id,
    appName:              item.name ?? item.appName ?? item.siteName ?? '—',
    location:             item.location ?? '—',
    resourceGroup:        item.resourceGroup ?? '—',
    tier:                 item.skuName ?? item.sku ?? '—',
    skuTier:              item.skuTier ?? '—',
    recommendedSkuName:   item.recommendedSkuName ?? '—',
    customDomains:        toNum(item.customDomainsCount ?? item.customDomains) ?? 0,
    bandwidth:            toNum(item.bandwidthGB ?? item.bandwidth) ?? 0,
    cdnStatus:            item.enterpriseGradeCdnStatus ?? '—',
    stagingPolicy:        item.stagingEnvironmentPolicy ?? '—',
    provider:             item.provider ?? '—',
    branch:               item.branch ?? '—',
    repositoryUrl:        item.repositoryUrl ?? '—',
    cost:                 `$${current.toFixed(2)}`,
    costValue:            current,
    recommendedCost:      `$${recommended.toFixed(2)}`,
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving:               formatSavingStr(saving),
    savingValue:          saving,
    actionLabel:          'Assist with AI',
    actionClass:          'mini-btn ai-assist',
  };
};

const isFree = (tier) => (tier ?? '').toLowerCase() === 'free';

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, rows) => {
  const rowCurrent  = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec      = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const rowSaving   = rows.reduce((s, r) => s + r.savingValue, 0);

  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : rowSaving;

  const standardCount = rows.filter((r) => !isFree(r.tier)).length;
  const freeCount     = rows.filter((r) => isFree(r.tier)).length;
  const downgradeRows = rows.filter((r) => ['DOWNGRADE', 'DOWNSIZE', 'RIGHTSIZE'].includes(r.recType));

  // 4th card left to the helper default: "Total Payable (after savings)"
  return buildBaseCostKpis({
    countTitle:    'Static Web Apps',
    countValue:    rows.length,
    countSubtitle: `${standardCount} Standard · ${freeCount} Free · ${downgradeRows.length} to downgrade`,
    current, saving, recommended,
  });
};

// ── Charts ────────────────────────────────────────────────────

const buildTierDonut = (rows) => {
  if (!rows.length) return null;
  const hasCost = rows.some((r) => r.costValue > 0);
  const buckets = {};
  rows.forEach((r) => {
    const t = r.tier && r.tier !== '—' ? r.tier : 'Unknown';
    buckets[t] = (buckets[t] || 0) + (hasCost ? r.costValue : 1);
  });
  const labels = Object.keys(buckets);
  if (!labels.length) return null;
  const palette = { Standard: '#0078d4', Free: '#2ea66f', Unknown: '#9aa3b7' };
  return {
    labels,
    datasets: [{
      data: labels.map((k) => parseFloat(buckets[k].toFixed(2))),
      backgroundColor: labels.map((k) => palette[k] ?? '#f57c00'),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
};

const buildSavingsByAction = (rows) => {
  const buckets = { 'Downgrade to Free': 0, 'Delete idle': 0 };
  rows.forEach((r) => {
    const s = r.savingValue;
    if (s <= 0) return;
    if (DELETE_RECS.includes(r.recType)) buckets['Delete idle'] += s;
    else buckets['Downgrade to Free'] += s;
  });
  if (!Object.values(buckets).some((v) => v > 0)) return null;
  return {
    labels: Object.keys(buckets),
    datasets: [{
      label: 'Saving ($)',
      data: Object.values(buckets).map((v) => parseFloat(v.toFixed(2))),
      backgroundColor: ['#f57c00', '#ef4444'],
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildBandwidthBar = (rows) => {
  const withBw = rows.filter((r) => Number(r.bandwidth) > 0).sort((a, b) => b.bandwidth - a.bandwidth).slice(0, 6);
  if (!withBw.length) return null;
  return {
    labels: withBw.map((r) => (r.appName.length > 16 ? r.appName.slice(0, 15) + '…' : r.appName)),
    datasets: [{
      label: 'Bandwidth (GB)',
      data: withBw.map((r) => parseFloat(r.bandwidth.toFixed(2))),
      backgroundColor: '#0078d4',
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

// Current vs recommended (payable) monthly cost per app — the downgrade savings story.
const buildCostCompare = (rows) => {
  const named = rows
    .filter((r) => r.costValue > 0 || r.recommendedCostValue > 0)
    .sort((a, b) => b.costValue - a.costValue)
    .slice(0, 8);
  if (!named.length) return null;
  return {
    labels: named.map((r) => (r.appName.length > 16 ? r.appName.slice(0, 15) + '…' : r.appName)),
    datasets: [
      { label: 'Current',     data: named.map((r) => parseFloat(r.costValue.toFixed(2))),            backgroundColor: '#0078d4', borderRadius: 3, barPercentage: 0.75 },
      { label: 'Recommended', data: named.map((r) => parseFloat(r.recommendedCostValue.toFixed(2))), backgroundColor: '#2ea66f', borderRadius: 3, barPercentage: 0.75 },
    ],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows.filter((r) => r.savingValue > 0).sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →'; let actionClass = 'mini-btn';
    if (DELETE_RECS.includes(r.recType)) { actionLabel = 'Delete →'; actionClass = 'mini-btn ghost'; }
    else if (['DOWNGRADE', 'DOWNSIZE', 'RIGHTSIZE'].includes(r.recType)) actionLabel = 'Downgrade →';
    return { id: i + 1, title: r.appName || r.id, amount: r.saving, desc: `${r.tier} · ${r.location}`, actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

// ── Cells ─────────────────────────────────────────────────────

const TierCell = ({ row }) => {
  const free = isFree(row.tier);
  return (
    <span style={{ background: free ? '#f0fdf4' : '#eff6ff', color: free ? '#2e7d32' : '#1565c0', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
      {row.tier}
    </span>
  );
};

// ── Table Columns ─────────────────────────────────────────────

const appColumns = [
  { key: 'id',            label: 'Id', render: (row) => <IdCell row={row} /> },
  { key: 'appName',       label: 'App Name' },
  { key: 'location',      label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group' },
  { key: 'tier',          label: 'Tier', render: (row) => <TierCell row={row} /> },
  {
    key: 'recommendedSkuName', label: 'Recommended SKU',
    render: (row) => (row.recommendedSkuName && row.recommendedSkuName !== row.tier)
      ? <span style={{ fontWeight: 600 }}>{row.recommendedSkuName}</span>
      : row.recommendedSkuName,
  },
  {
    key: 'provider', label: 'Source',
    render: (row) => (row.provider && row.provider !== '—')
      ? <span title={row.repositoryUrl}>{row.provider}{row.branch && row.branch !== '—' ? ` · ${row.branch}` : ''}</span>
      : <span style={{ color: 'var(--muted)' }}>—</span>,
  },
  { key: 'customDomains', label: 'Custom Domains', className: 'num' },
  { key: 'bandwidth',     label: 'Bandwidth (GB)', className: 'num', render: (row) => row.bandwidth != null ? row.bandwidth : '—' },
  { key: 'cdnStatus',     label: 'CDN' },
  { key: 'cost',                 label: 'Cost / Mo',         className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation',    render: (row) => <RecCell row={row} /> },
  { key: 'saving',               label: 'Saving / Mo',       className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',      label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action', label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────

const AzureStaticWebAppOptimization = () => {
  const { selectedAccount } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [rows,      setRows]      = useState([]);
  const [kpi,       setKpi]       = useState([]);

  const [tierData,        setTierData]        = useState(null);
  const [savingsData,     setSavingsData]     = useState(null);
  const [bandwidthData,   setBandwidthData]   = useState(null);
  const [costCompareData, setCostCompareData] = useState(null);
  const [insightActions,  setInsightActions]  = useState([]);
  const [insightTotal,    setInsightTotal]    = useState(0);

  const [tagList,         setTagList]         = useState([]);
  const [filterList,      setFilterList]      = useState({ tag: '', values: [] });
  const [locationFilter,  setLocationFilter]  = useState('');
  const [tierFilter,      setTierFilter]      = useState('');
  const [providerFilter,  setProviderFilter]  = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      appId: row.id, appName: row.appName, location: row.location, resourceGroup: row.resourceGroup,
      tier: row.tier, recommendedSku: row.recommendedSkuName, customDomains: row.customDomains,
      bandwidthGB: row.bandwidth, cdnStatus: row.cdnStatus, provider: row.provider,
      currentMonthlyCost: row.cost, recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the static web app data provided, give a concise, natural insight ' +
      'directly to the user — mention whether the Standard tier is justified, potential savings from downgrading, ' +
      'and what they should consider doing. Keep it conversational and to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchTagList = () => {
    if (!selectedAccount) return;
    setTagList([]);
    if (filterList.tag !== '' || filterList.values.length > 0) setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: 'STATIC_WEB_APP_SERVICE', userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  const fetchData = (isForceRefresh = false) => {
    if (!selectedAccount || isLoading) return;
    const userId = getUserId();
    if (!userId) return;
    setIsLoading(true);
    setRows([]);
    const reqObj = {
      userId, type: 'CUSTOM_STATIC_WEB_APP_SERVICE', userAccountId: selectedAccount,
      filterKey:    filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    };
    if (isForceRefresh) reqObj.isForceRefresh = true;
    RecommendationApiService.getAzureData(reqObj)
      .then((res) => {
        const mapped = (res?.data ?? []).map(toStaticWebAppRow);
        setRows(mapped);
        setKpi(buildKpiData(res?.totalMonthlyCost, mapped));
      })
      .catch((err) => console.error('Error fetching static web app recommendations', err))
      .finally(() => setIsLoading(false));
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag')    { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) { setFilterList({ tag: '', values: [] }); fetchData(); fetchTagList(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const locations = useMemo(() => [...new Set(rows.map((r) => r.location).filter((v) => v && v !== '—'))].sort(), [rows]);
  const tiers     = useMemo(() => [...new Set(rows.map((r) => r.tier).filter((v) => v && v !== '—'))].sort(), [rows]);
  const providers = useMemo(() => [...new Set(rows.map((r) => r.provider).filter((v) => v && v !== '—'))].sort(), [rows]);

  const filteredRows = useMemo(() => {
    let r = rows;
    if (locationFilter) r = r.filter((row) => row.location === locationFilter);
    if (tierFilter)     r = r.filter((row) => row.tier === tierFilter);
    if (providerFilter) r = r.filter((row) => row.provider === providerFilter);
    return r;
  }, [rows, locationFilter, tierFilter, providerFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setTierData(buildTierDonut(filteredRows));
      setSavingsData(buildSavingsByAction(filteredRows));
      setBandwidthData(buildBandwidthBar(filteredRows));
      setCostCompareData(buildCostCompare(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions); setInsightTotal(totalSaving);
    } else {
      setTierData(null); setSavingsData(null); setBandwidthData(null); setCostCompareData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [filteredRows]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Static Web App Optimization',
      kpi,
      staticWebAppRecommendations: filteredRows,
    });
  }, [filteredRows, kpi]);

  const handleExport = () =>
    exportCSV(activeRows, appColumns.filter((c) => c.key !== 'action'), 'azure-static-web-app.csv');

  const clearLocalFilters = () => {
    setLocationFilter(''); setTierFilter(''); setProviderFilter('');
    setFilterList({ tag: '', values: [] });
  };

  const showClear = !!(filterList.tag || locationFilter || tierFilter || providerFilter);

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Static Web App</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Static Web App Optimization{' '}
            {isLoading ? <BadgeSkeleton /> : <span className="badge">{rows.length} static apps</span>}
          </h1>
          <div className="meta">Azure Static Web Apps · downgrade idle Standard apps to Free tier</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={() => { fetchData(true); fetchTagList(); }} disabled={isLoading}>↻ {isLoading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <KpiRow kpiData={kpi} loading={isLoading} />

      {/* Charts */}
      <div className="grid-3" style={{ marginTop: '4px' }}>
        <Card className="first" title="Apps by tier" subtitle="Standard vs Free distribution by cost">
          <DonutChart data={tierData} loading={isLoading} />
        </Card>
        <Card className="first" title="Recommended savings by action" subtitle="Where the savings come from">
          <HorizontalBarChart data={savingsData} loading={isLoading} />
        </Card>
        <TopActionsCard
          title="Top static app actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoading}
          entityName="static apps"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Tier</label>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="">All</option>
            {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Source</label>
          <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
            <option value="">All</option>
            {providers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="spacer" />
        {showClear && <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>}
        <button className="btn ghost" onClick={() => { fetchData(true); fetchTagList(); }} disabled={isLoading}>↻ {isLoading ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      <Table
        rows={activeRows}
        columns={appColumns}
        loading={isLoading}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No static web app recommendations"
      />

      {/* Bottom charts */}
      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Bandwidth by app (top 6)" subtitle="Data transfer in GB served">
          <HorizontalBarChart data={bandwidthData} loading={isLoading} xTickCallback={(v) => v + ' GB'} />
        </Card>
        <Card className="first" title="Current vs recommended cost" subtitle="Monthly cost before vs after downgrade (top 8)">
          <GroupedBarChart data={costCompareData} loading={isLoading} ySuffix="" yPrefix="$" />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default AzureStaticWebAppOptimization;
